import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
const DEFAULT_CAROUSEL_CONFIG = {
    loop: false,
    align: "center",
    speed: 12,
    autoplay: false,
    displayCardCount: false,
    columnCount: { mobile: 1, tablet: 2, desktop: 3 },
    dragFree: true,
    containScroll: "trimSnaps",
};
const Carousel = ({ items, buttonType = "button", config = {} }) => {
    const carouselConfig = useMemo(() => {
        const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
        return Object.assign(Object.assign(Object.assign({}, DEFAULT_CAROUSEL_CONFIG), config), { align: isMobile ? "center" : "start" });
    }, [config]);
    const columnCounts = useMemo(() => {
        var _a, _b, _c, _d;
        const cols = (_a = config.columnCount) !== null && _a !== void 0 ? _a : DEFAULT_CAROUSEL_CONFIG.columnCount;
        if (typeof cols === "number") {
            return { mobile: 1, tablet: cols, desktop: cols };
        }
        return {
            mobile: (_b = cols.mobile) !== null && _b !== void 0 ? _b : 1,
            tablet: (_c = cols.tablet) !== null && _c !== void 0 ? _c : 2,
            desktop: (_d = cols.desktop) !== null && _d !== void 0 ? _d : 3,
        };
    }, [config.columnCount]);
    const clampedCols = useMemo(() => ({
        mobile: Math.min(columnCounts.mobile, items.length),
        tablet: Math.min(columnCounts.tablet, items.length),
        desktop: Math.min(columnCounts.desktop, items.length),
    }), [columnCounts, items.length]);
    const gridColsMap = {
        1: "grid-cols-1",
        2: "grid-cols-2",
        3: "grid-cols-3",
        4: "grid-cols-4",
        5: "grid-cols-5",
        6: "grid-cols-6",
    };
    const mdGridColsMap = {
        1: "md:grid-cols-1",
        2: "md:grid-cols-2",
        3: "md:grid-cols-3",
        4: "md:grid-cols-4",
        5: "md:grid-cols-5",
        6: "md:grid-cols-6",
    };
    const lgGridColsMap = {
        1: "lg:grid-cols-1",
        2: "lg:grid-cols-2",
        3: "lg:grid-cols-3",
        4: "lg:grid-cols-4",
        5: "lg:grid-cols-5",
        6: "lg:grid-cols-6",
    };
    const gridClasses = [
        gridColsMap[clampedCols.mobile] || "grid-cols-1",
        mdGridColsMap[clampedCols.tablet] || "md:grid-cols-2",
        lgGridColsMap[clampedCols.desktop] || "lg:grid-cols-3",
    ].join(" ");
    const [emblaRef, emblaApi] = useEmblaCarousel(carouselConfig);
    const [canScrollPrev, setCanScrollPrev] = useState(true);
    const [canScrollNext, setCanScrollNext] = useState(true);
    const [showButtons, setShowButtons] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState([]);
    const [allItemsFit, setAllItemsFit] = useState(false);
    const scrollPrev = () => emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev(true);
    const scrollNext = () => emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext(true);
    const scrollTo = (index) => emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollTo(index);
    useEffect(() => {
        if (!emblaApi)
            return;
        const updateScrollState = () => {
            setCanScrollPrev(emblaApi.canScrollPrev());
            setCanScrollNext(emblaApi.canScrollNext());
            setSelectedIndex(emblaApi.selectedScrollSnap());
            setScrollSnaps(emblaApi.scrollSnapList());
            const canScroll = emblaApi.canScrollPrev() || emblaApi.canScrollNext();
            setAllItemsFit(!canScroll); // If cannot scroll, items all fit
            setShowButtons(canScroll);
        };
        emblaApi.on("init", updateScrollState);
        emblaApi.on("select", updateScrollState);
        emblaApi.on("reInit", updateScrollState);
        return () => {
            emblaApi.off("init", updateScrollState);
            emblaApi.off("select", updateScrollState);
            emblaApi.off("reInit", updateScrollState);
        };
    }, [emblaApi]);
    const columnCount = clampedCols.desktop;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: `hidden ${carouselConfig.carouselOnDesktop === false ? "lg:grid" : ""} ${gridClasses} gap-md m-auto h-full `, children: items }), _jsxs("section", { className: `w-full ${carouselConfig.carouselOnDesktop === false ? "lg:hidden" : "lg:block"}`, children: [_jsx("div", { ref: emblaRef, className: "relative overflow-hidden", children: _jsx("div", { className: `flex gap-md ${allItemsFit ? "justify-center" : "justify-start"}`, children: items.map((item, index) => (_jsx("div", { className: "flex-shrink-0 lg:p-0", children: item }, index))) }) }), showButtons && columnCount > 1 && buttonType === "button" && (_jsxs("div", { className: "flex flex-col", children: [config.displayCardCount && (_jsxs("div", { className: "my-8", children: [selectedIndex + 1, " of ", items.length] })), _jsxs("div", { className: `flex justify-center gap-4 ${config.displayCardCount ? "mt-2" : "mt-8"}`, children: [_jsx("button", { className: `p-2 rounded-full shadow mr-2 h-[40px] w-[40px] ${canScrollPrev
                                            ? config.btnStyle || "bg-is2-blue text-white"
                                            : "bg-is2-blue-100 text-gray-400"}`, onClick: scrollPrev, disabled: !canScrollPrev, children: _jsx(ChevronLeftIcon, { className: "h-5 w-5 m-auto" }) }), _jsx("button", { className: `p-2 rounded-full shadow h-[40px] w-[40px] ${canScrollNext
                                            ? config.btnStyle || "bg-is2-blue text-white"
                                            : "bg-is2-blue-100 text-gray-400"}`, onClick: scrollNext, disabled: !canScrollNext, children: _jsx(ChevronRightIcon, { className: "h-5 w-5 m-auto" }) })] })] })), showButtons && buttonType === "circle" && (_jsx("div", { className: "flex justify-center mt-2 space-x-2", children: scrollSnaps.map((_, index) => (_jsx("button", { onClick: () => scrollTo(index), className: `w-2.5 h-2.5 rounded-full transition-colors ${index === selectedIndex ? "bg-is2-blue" : "bg-is2-blue-100"}` }, index))) }))] })] }));
};
export default Carousel;
